import React, { useState } from 'react';
import AuctionMap from '../../../components/AuctionMap';
import { styles } from './adminStyles';
import api from '../../../services/api';
import { getDisplayStatus } from '../../../config/eventStatus';
import { getAuctionTypeLabel } from '../../../config/auctionTypeConfig';
import AdminBidOverrideModal from './AdminBidOverrideModal';
import AdminBackfillModal from './AdminBackfillModal';

// Uploaded attachment `url`s are stored as relative paths (e.g. /uploads/bids/xyz.pdf)
// — resolve them against the API's origin (stripping the trailing /api) so they're
// directly clickable from the browser. Mirrors AuctionBiddingView.js's UPLOADS_BASE_URL.
const UPLOADS_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const DECISION_COLORS = { pending: '#475569', approved: '#0D9488', rejected: '#F59E0B', explore: '#163321' };
const DECISION_LABELS = { pending: 'Pending Review', approved: 'Approved', rejected: 'Rejected', explore: 'Explore / Under Discussion' };

// Manual Bid Override & Winner Promotion badges — surfaced next to a bid's
// normal status pill whenever an admin has hand-overridden it post-closure.
// Tooltip pulls the most recent matching entry off manualOverrideHistory so
// the admin can see who/when/why without opening a separate audit view.
const OVERRIDE_BADGE_CONFIG = {
    withdrawn_winner: { label: '⚠ Manually Withdrawn', color: '#F59E0B' },
    promoted_winner: { label: '⭐ Manually Promoted', color: '#0D9488' },
};
const OverrideBadge = ({ bid }) => {
    const config = OVERRIDE_BADGE_CONFIG[bid.manualOverrideStatus];
    if (!config) return null;
    const historyAction = bid.manualOverrideStatus === 'withdrawn_winner' ? 'withdraw_winner' : 'promote_bid';
    const lastEntry = [...(bid.manualOverrideHistory || [])].reverse().find(h => h.action === historyAction);
    const tooltip = lastEntry
        ? `${new Date(lastEntry.timestamp).toLocaleString('en-GB')}: ${lastEntry.reason}`
        : undefined;
    return (
        <span
            title={tooltip}
            style={{ marginLeft: '6px', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#FFFFFF', color: config.color, border: `1px solid ${config.color}` }}
        >
            {config.label}
        </span>
    );
};

// One bid/action row within a farmer's group — a scannable collapsed row;
// "Expand Details" opens a modal with the full proposal text, attachments, and the
// Update Decision panel (Approve/Explore/Reject selection + comments + Save)
// together. Comments/selection are kept in local draft state so the admin can
// type/pick freely before saving. Saving here only stages the decision; emails go
// out in batch when the auction closes.
const BidActionRow = ({ bid, onSave, onDelete, isClosed, onOpenOverride }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState(bid.decisionStatus || 'pending');
    const [comments, setComments] = useState(bid.adminDecisionComments || '');
    const [isSaving, setIsSaving] = useState(false);
    const decisionStatus = bid.decisionStatus || 'pending';

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(bid._id, selectedDecision, comments);
        } finally {
            setIsSaving(false);
        }
    };

    const proposalSections = [
        bid.proposalOverview && { key: 'overview', label: 'Overview', value: bid.proposalOverview },
        bid.impactStatement && { key: 'impact', label: 'Outcomes', value: bid.impactStatement },
        bid.evaluationStatement && { key: 'evaluation', label: 'Evaluation', value: bid.evaluationStatement }
    ].filter(Boolean);

    const decisionButtonStyle = (status, disabled) => ({
        flex: 1,
        padding: '10px 16px',
        borderRadius: '4px',
        fontWeight: 'bold',
        fontFamily: '"Inter", sans-serif',
        fontSize: '0.9rem',
        border: `1px solid ${DECISION_COLORS[status]}`,
        backgroundColor: selectedDecision === status ? DECISION_COLORS[status] : '#FFFFFF',
        color: selectedDecision === status ? '#FFFFFF' : DECISION_COLORS[status],
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1
    });
    const decisionDisabled = isClosed || isSaving;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '12px 20px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ flex: 2, fontWeight: 'bold', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem' }}>
                {bid.actionName || 'Unknown Action'}
            </div>
            <div style={{ flex: 1, fontWeight: 'bold', color: '#0D9488', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem' }}>
                £{(bid.bidPrice ?? bid.amount ?? 0).toLocaleString('en-GB')}
                {bid.benefitCostRatio != null && (
                    <div style={{ fontSize: '0.7rem', fontWeight: 'normal', color: '#475569' }}>
                        BCR: {bid.benefitCostRatio.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}:1
                    </div>
                )}
            </div>
            <div style={{ flex: 1 }}>
                <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif', backgroundColor: '#FFFFFF', border: `1px solid ${DECISION_COLORS[decisionStatus]}`, color: DECISION_COLORS[decisionStatus] }}>
                    {DECISION_LABELS[decisionStatus]}
                </span>
                <OverrideBadge bid={bid} />
            </div>
            <div style={{ flexShrink: 0, display: 'flex', gap: '8px' }}>
                <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem' }}
                >
                    Expand Details ▾
                </button>
                {isClosed && onOpenOverride && bid.status === 'accepted' && (
                    <button
                        type="button"
                        onClick={() => onOpenOverride('withdraw', bid)}
                        title="Withdraw this winning bid post-closure"
                        style={{ ...styles.btnDangerOutline, borderColor: '#F59E0B', color: '#F59E0B', padding: '6px 12px', fontSize: '0.8rem' }}
                    >
                        Withdraw Winner
                    </button>
                )}
                {isClosed && onOpenOverride && ['rejected', 'explore'].includes(bid.status) && (
                    <button
                        type="button"
                        onClick={() => onOpenOverride('promote', bid)}
                        title="Manually promote this bid to Winning"
                        style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem' }}
                    >
                        Promote to Winner
                    </button>
                )}
                {onDelete && (
                    <button
                        type="button"
                        disabled={isClosed}
                        onClick={() => onDelete(bid)}
                        title="Force-delete this bid"
                        style={{ ...styles.btnDangerOutline, padding: '6px 12px', fontSize: '0.8rem', opacity: isClosed ? 0.5 : 1, cursor: isClosed ? 'not-allowed' : 'pointer' }}
                    >
                        Delete
                    </button>
                )}
            </div>

            {isModalOpen && (
                <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
                    <div style={{ ...styles.modal, width: '700px', height: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0 }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                    <h2 style={{ margin: 0, ...styles.h2 }}>{bid.actionName || 'Unknown Action'}</h2>
                                    <span style={{ fontWeight: 'bold', color: '#0D9488', fontSize: '1.1rem', fontFamily: '"Inter", sans-serif' }}>£{(bid.bidPrice ?? bid.amount ?? 0).toLocaleString('en-GB')}</span>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#475569', fontFamily: '"Inter", sans-serif', marginTop: '2px' }}>{bid.user?.name || 'Unknown Farmer'}</div>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC' }}>
                            {proposalSections.length > 0 && (
                                <div style={{ backgroundColor: '#FFFFFF', border: '1px solid #eee', borderRadius: '6px', padding: '15px', marginBottom: '15px' }}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#163321', fontFamily: '"Inter", sans-serif', marginBottom: '10px' }}>Proposal Details</div>
                                    {proposalSections.map((section, idx) => (
                                        <div key={section.key} style={{ marginTop: idx === 0 ? 0 : '12px', paddingTop: idx === 0 ? 0 : '12px', borderTop: idx === 0 ? 'none' : '1px solid #E2E8F0' }}>
                                            <label style={styles.label}>{section.label}</label>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#0F172A', fontFamily: '"Inter", sans-serif', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{section.value}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {bid.attachments?.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={styles.label}>Attachments</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                        {bid.attachments.map((att, i) => (
                                            <a
                                                key={i}
                                                href={`${UPLOADS_BASE_URL}${att.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ ...styles.btnReversed, padding: '4px 10px', fontSize: '0.75rem', textDecoration: 'none' }}
                                            >
                                                📎 {att.filename}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div style={{ backgroundColor: '#FFFFFF', border: '1px solid #eee', borderRadius: '6px', padding: '15px' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#163321', fontFamily: '"Inter", sans-serif', marginBottom: '10px' }}>Update Decision</div>
                                <label style={styles.label}>Decision</label>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                    <button
                                        type="button"
                                        disabled={decisionDisabled}
                                        onClick={() => setSelectedDecision('approved')}
                                        style={decisionButtonStyle('approved', decisionDisabled)}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        disabled={decisionDisabled}
                                        onClick={() => setSelectedDecision('explore')}
                                        style={decisionButtonStyle('explore', decisionDisabled)}
                                    >
                                        Explore
                                    </button>
                                    <button
                                        type="button"
                                        disabled={decisionDisabled}
                                        onClick={() => setSelectedDecision('rejected')}
                                        style={decisionButtonStyle('rejected', decisionDisabled)}
                                    >
                                        Reject
                                    </button>
                                </div>
                                <label style={styles.label}>Decision Comments</label>
                                <textarea
                                    value={comments}
                                    disabled={isClosed}
                                    onChange={(e) => setComments(e.target.value)}
                                    placeholder="Feedback, negotiation notes, or terms..."
                                    style={{ ...styles.input, height: '60px', resize: 'vertical', marginBottom: '10px', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF' }}
                                />
                                <button
                                    type="button"
                                    disabled={decisionDisabled}
                                    onClick={handleSave}
                                    style={{ ...styles.btnPrimary, width: '100%', opacity: decisionDisabled ? 0.6 : 1, cursor: decisionDisabled ? 'not-allowed' : 'pointer' }}
                                >
                                    {isSaving ? 'Saving...' : 'Save Decision'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// All of one farmer's Whole Farm bids, grouped under a single header: name, total
// value across every action bid on, and a single "View on Map" button (every bid
// from this farmer shares the same fieldLocation — the farmer's first field).
// Rows below are arranged by action name, one per bid.
const FarmerBidGroup = ({ farmerName, bids, onSave, onDelete, isClosed, onZoomToField, onOpenOverride }) => {
    const totalValue = bids.reduce((sum, b) => sum + (b.bidPrice ?? b.amount ?? 0), 0);
    const fieldLocation = bids.find(b => b.fieldLocation)?.fieldLocation;
    const sortedBids = [...bids].sort((a, b) => (a.actionName || '').localeCompare(b.actionName || ''));

    return (
        <div style={{ ...styles.card, marginBottom: 0, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', backgroundColor: '#F8FAFC', borderBottom: '1px solid #eee' }}>
                <div>
                    <h3 style={{ margin: 0, ...styles.h3 }}>Bids from {farmerName}</h3>
                    <div style={{ fontSize: '0.9rem', color: '#0D9488', fontWeight: 'bold', fontFamily: '"Inter", sans-serif', marginTop: '4px' }}>
                        Total: £{totalValue.toLocaleString('en-GB')}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onZoomToField}
                    disabled={!fieldLocation}
                    title={fieldLocation ? 'Zoom to Field' : 'No field location available'}
                    style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', opacity: fieldLocation ? 1 : 0.5, cursor: fieldLocation ? 'pointer' : 'not-allowed' }}
                >
                    🔍 View on Map
                </button>
            </div>
            <div>
                {sortedBids.map(bid => (
                    <BidActionRow key={bid._id} bid={bid} onSave={onSave} onDelete={onDelete} isClosed={isClosed} onOpenOverride={onOpenOverride} />
                ))}
            </div>
        </div>
    );
};

const AdminBidDetails = ({
  liveBids, bidsLoading, handleSaveDecision, handleDeleteBid,
  handleWithdrawWinner, handlePromoteBid, fetchPromotionHeadroom,
  fetchBackfillPreview, handleBackfillWinners,
  selectedEvent, setSelectedEvent, events, currentEv, isClosed, isManual, zones,
  auctionId, isModal
}) => {
    const [focusParcel, setFocusParcel] = useState(null);
    const [zoomTarget, setZoomTarget] = useState(null);
    const [isExportingZip, setIsExportingZip] = useState(false);
    const [expandedBreakdownId, setExpandedBreakdownId] = useState(null);
    const [overrideModal, setOverrideModal] = useState(null); // { mode: 'withdraw'|'promote', bid }
    const [isBackfillModalOpen, setIsBackfillModalOpen] = useState(false);
    const lockedToAuction = isModal || !!auctionId;
    const effectiveAuctionId = auctionId || selectedEvent;

    // Mirrors SuperAdminDashboard's Bid Force-Delete prompt — the reason is
    // required and gets emailed to the bidder plus every admin/superadmin.
    const promptDeleteBid = (bid) => {
        if (!handleDeleteBid) return;
        const reason = window.prompt(`Deleting bid £${(bid.payoutAmount ?? bid.amount ?? 0).toLocaleString('en-GB')} from ${bid.user?.name || 'Unknown Farmer'}.\n\nREQUIRED: Enter reason for deletion (sent to the farmer):`);
        if (!reason || !reason.trim()) return;
        handleDeleteBid(bid._id, reason, auctionId || selectedEvent);
    };

    // Manual Bid Override & Winner Promotion (post-closure only — see isClosed
    // gates below). openOverrideModal is only wired up when the parent passed
    // handlers, so this feature degrades to a no-op if AdminDashboard hasn't.
    const canOverride = !!(handleWithdrawWinner && handlePromoteBid && fetchPromotionHeadroom);
    const openOverrideModal = canOverride ? (mode, bid) => setOverrideModal({ mode, bid }) : undefined;

    // Automated backfill (see backend/routes/admin.js's computeBackfillCandidates) —
    // excluded for Manual (Whole Farm) auctions, whose decisionStatus reflects a
    // deliberate admin review call rather than a pure ranking cutoff.
    const canBackfill = !!(fetchBackfillPreview && handleBackfillWinners) && isClosed && !isManual;

    const handleExportZip = async () => {
        if (!auctionId || isExportingZip) return;
        setIsExportingZip(true);
        try {
            const res = await api.get(`/admin/bids/${auctionId}/export-zip`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `manual_proposals_${auctionId}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // With responseType: 'blob', axios decodes an error response's body as a
            // Blob too (even for a JSON error payload) — parse it back to text/JSON
            // here rather than surfacing an opaque "[object Blob]" to the admin.
            let parsedError = null;
            if (err.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    try {
                        parsedError = JSON.parse(text);
                    } catch {
                        parsedError = { message: text };
                    }
                } catch (parseErr) {
                    console.error('Failed to parse ZIP export error response:', parseErr);
                }
            }
            console.error('ZIP export failed:', parsedError || err);
            alert(parsedError?.error || parsedError?.msg || parsedError?.message || 'Export failed.');
        } finally {
            setIsExportingZip(false);
        }
    };

    const manualBids = liveBids.filter(bid => bid.status !== 'draft');
    const farmerGroupsMap = {};
    manualBids.forEach(bid => {
        const farmerId = bid.user?._id || bid.user?.email || 'unknown';
        if (!farmerGroupsMap[farmerId]) {
            farmerGroupsMap[farmerId] = { farmerId, farmerName: bid.user?.name || 'Unknown Farmer', bids: [] };
        }
        farmerGroupsMap[farmerId].bids.push(bid);
    });
    const farmerGroups = Object.values(farmerGroupsMap);

    const groupedBids = {};
    liveBids.forEach(bid => {
        const action = bid.actionName || 'Unknown Action';
        if (!groupedBids[action]) groupedBids[action] = [];

        // Phase 5: benefitCostRatio/calculatedBenefit are now persisted directly
        // by the formula evaluator (backend/services/formulaEvaluator.js) at bid
        // submission and at /run|/commit — no client-side score guesswork needed.
        const benefitCostRatio = bid.benefitCostRatio ?? 0;
        const isEligible = benefitCostRatio > 0;

        let qty = 1;
        if (bid.unit === 'ha' && bid.parcel?.area) qty = bid.parcel.area;
        else if (bid.units) qty = bid.units;
        const pricePerUnit = qty > 0 ? (bid.amount / qty) : 0;

        groupedBids[action].push({
            ...bid,
            calcPricePerUnit: pricePerUnit,
            isEligible
        });
    });

    Object.keys(groupedBids).forEach(action => {
        groupedBids[action].sort((a, b) => (b.benefitCostRatio ?? 0) - (a.benefitCostRatio ?? 0));
    });

    const sortedActionGroups = Object.entries(groupedBids).sort((a, b) => b[1].length - a[1].length);

    const thStyle = { backgroundColor: '#FFFFFF', color: '#475569', borderBottom: '2px solid #eee', padding: '8px 10px', fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', textAlign: 'left' };
    const tdStyle = { ...styles.td, fontSize: '0.8rem', padding: '8px 10px' };
    const showActionsColumn = !!handleDeleteBid || (canOverride && isClosed);

    const bidDetailsDisplayStatus = currentEv && getDisplayStatus(currentEv);
    const bidDetailsStatusBadgeStyle = {
        active: { backgroundColor: '#E6F4F1', color: '#0D9488' },
        ended: { backgroundColor: '#F59E0B', color: '#FFFFFF' },
        closed: { backgroundColor: '#0F172A', color: '#FFFFFF' },
    }[bidDetailsDisplayStatus] || { backgroundColor: '#E6F4F1', color: '#0D9488' };
    const bidDetailsStatusBadgeText = bidDetailsDisplayStatus === 'ended' ? 'ENDED - AWAITING REVIEW' : currentEv?.status.toUpperCase();

    const statusTypeBadges = currentEv && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginLeft: lockedToAuction ? 0 : 'auto', flexShrink: 0 }}>
            {!lockedToAuction && <h3 style={{ margin: 0, color: '#163321', fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1rem' }}>{currentEv.name}</h3>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', color: '#475569', borderLeft: lockedToAuction ? 'none' : '2px solid #ddd', paddingLeft: lockedToAuction ? 0 : '15px' }}>
                <span>Status:</span>
                <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', ...bidDetailsStatusBadgeStyle }}>
                    {bidDetailsStatusBadgeText}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', color: '#475569', borderLeft: '2px solid #ddd', paddingLeft: '15px' }}>
                <span>Type:</span>
                <span style={{ fontSize: '0.95rem', color: '#0F172A', fontWeight: 'bold' }}>
                    {getAuctionTypeLabel(currentEv.auctionType)}
                </span>
            </div>
        </div>
    );

    const zipExportButton = isManual && (
        <button
            type="button"
            disabled={!auctionId || isExportingZip}
            title={auctionId ? 'Download all proposals and attachments as a ZIP' : 'No event selected'}
            onClick={handleExportZip}
            style={{ ...styles.btnReversed, marginLeft: 'auto', flexShrink: 0, opacity: (!auctionId || isExportingZip) ? 0.6 : 1, cursor: (!auctionId || isExportingZip) ? 'not-allowed' : 'pointer' }}
        >
            {isExportingZip ? 'Generating ZIP...' : '⬇ Download Bid Info (ZIP)'}
        </button>
    );

    const backfillButton = canBackfill && (
        <button
            type="button"
            title="Re-rank standby bids against current budget/cap headroom and promote whichever fit"
            onClick={() => setIsBackfillModalOpen(true)}
            style={{ ...styles.btnReversed, marginLeft: 'auto', flexShrink: 0 }}
        >
            🔄 Backfill Winners
        </button>
    );

    return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        {lockedToAuction ? (
            (statusTypeBadges || zipExportButton || backfillButton) && (
                <div style={{display:'flex', alignItems:'center', backgroundColor:'#FFFFFF', padding:'12px 20px', borderRadius:'8px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', marginBottom: '20px', overflowX: 'auto', whiteSpace: 'nowrap'}}>
                    {statusTypeBadges}
                    {zipExportButton}
                    {backfillButton}
                </div>
            )
        ) : (
            <div style={{display:'flex', alignItems:'center', gap:'15px', backgroundColor:'#FFFFFF', padding:'15px 20px', borderRadius:'8px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', marginBottom: '25px', overflowX: 'auto', whiteSpace: 'nowrap', position: 'sticky', top: '10px', zIndex: 100}}>
                <h2 style={{color:'#0F172A', margin:0, flexShrink: 0, ...styles.h2}}>Bid Details</h2>
                <div style={{ width: '2px', height: '30px', backgroundColor: '#eee', margin: '0 5px', flexShrink: 0 }}></div>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#FFFFFF', flexShrink: 0 }}>
                    <span style={{ backgroundColor: '#0D9488', color: '#FFFFFF', padding: '10px 15px', fontSize: '0.9rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}>Select Event</span>
                    <select style={{ border: 'none', padding: '10px 15px', outline: 'none', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', width: '35ch', color: '#0F172A', backgroundColor: 'transparent' }} value={selectedEvent || ''} onChange={(e) => setSelectedEvent(e.target.value)}>
                        <option value="">-- Select Event --</option>
                        {events.map(ev => <option key={ev._id} value={ev._id}>{ev.name}</option>)}
                    </select>
                </div>

                {statusTypeBadges}
                {zipExportButton}
                {backfillButton}
            </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ ...styles.card, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '420px', flexShrink: 0, marginBottom: 0, position: 'sticky', top: 0, zIndex: 10 }}>
                <AuctionMap zones={zones} bids={liveBids} focusParcel={focusParcel} zoomTarget={zoomTarget} isManual={isManual} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {isManual ? (
                    bidsLoading ? (
                        <div style={styles.card}><p style={styles.textMuted}>Loading proposals...</p></div>
                    ) : farmerGroups.length === 0 ? (
                        <div style={styles.card}><p style={styles.textMuted}>No Whole Farm proposals submitted yet.</p></div>
                    ) : (
                        farmerGroups.map(group => (
                            <FarmerBidGroup
                                key={group.farmerId}
                                farmerName={group.farmerName}
                                bids={group.bids}
                                onSave={(bidId, decisionStatus, adminDecisionComments) => handleSaveDecision(bidId, decisionStatus, adminDecisionComments, auctionId)}
                                onDelete={handleDeleteBid ? promptDeleteBid : undefined}
                                isClosed={isClosed}
                                onZoomToField={() => setZoomTarget(group.bids.find(b => b.fieldLocation)?.fieldLocation)}
                                onOpenOverride={openOverrideModal}
                            />
                        ))
                    )
                ) : bidsLoading ? (
                    <div style={styles.card}><p style={styles.textMuted}>Loading bids...</p></div>
                ) : liveBids.length === 0 ? (
                    <div style={styles.card}><p style={styles.textMuted}>No bids found for this event.</p></div>
                ) : (
                    sortedActionGroups.map(([actionName, bidsList]) => (
                        <div key={actionName} style={{...styles.card, marginBottom: 0, padding: 0, overflow: 'hidden'}}>
                            <div style={{ backgroundColor: '#163321', color: '#FFFFFF', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ margin: 0, fontSize: '1.05rem', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{actionName}</h2>
                                <span style={{ fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '4px' }}>
                                    {bidsList.length} Bids
                                </span>
                            </div>
                            <div style={{overflowX: 'auto', padding: '10px'}}>
                                <table style={{width:'100%', borderCollapse:'collapse'}}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Farmer</th>
                                            <th style={thStyle}>Field</th>
                                            <th style={thStyle}>Qty</th>
                                            <th style={thStyle}>Price</th>
                                            <th style={thStyle}>Benefit £</th>
                                            <th style={{...thStyle, color: '#0D9488', borderBottom: '2px solid #0D9488'}}>Benefit:Cost<br/>Ratio</th>
                                            <th style={thStyle}>£/quantity</th>
                                            <th style={thStyle}>Map</th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>Breakdown</th>
                                            {showActionsColumn && <th style={thStyle}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bidsList.map((bid, i) => {
                                            const displayStatus = bid.status.toUpperCase();
                                            const statusColor = bid.status === 'accepted' ? '#0D9488' : bid.status === 'rejected' ? '#475569' : bid.status === 'withdrawn' ? '#DC2626' : '#F59E0B';

                                            return (
                                            <React.Fragment key={bid._id}>
                                            <tr style={{borderBottom:'1px solid #f9f9f9', backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#F8FAFC'}}>
                                                <td style={tdStyle}>
                                                    <strong>{bid.user?.name}</strong>
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: 'bold', color: '#0F172A' }}>{bid.parcel ? (bid.parcel.parcelRef || bid.parcel.parcelId) : 'Whole Farm'}</div>
                                                    {bid.parcel?.customName && <div style={{ fontSize: '0.75rem', color: '#163321' }}>{bid.parcel.customName}</div>}
                                                </td>
                                                <td style={tdStyle}>
                                                    {bid.unit === 'ha' ? (
                                                        <span>{bid.parcel?.area ? bid.parcel.area.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'} <span style={{fontSize:'0.7rem', color:'#475569'}}>ha</span></span>
                                                    ) : (
                                                        <span>{bid.units} <span style={{fontSize:'0.7rem', color:'#475569'}}>{bid.unitName || bid.unit}</span></span>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <strong>£{(bid.payoutAmount ?? bid.amount).toLocaleString('en-GB')}</strong>
                                                    {bid.payoutAmount != null && bid.payoutAmount !== bid.amount && (
                                                        <div style={{ fontSize: '0.7rem', color: '#475569' }}>Bid: £{bid.amount.toLocaleString('en-GB')}</div>
                                                    )}
                                                </td>

                                                {/* BENEFIT £ (calculatedBenefit) */}
                                                <td style={tdStyle}>
                                                    {bid.isEligible ? (
                                                        <span style={{fontWeight:'bold', color: '#0F172A'}}>
                                                            £{(bid.calculatedBenefit || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}
                                                        </span>
                                                    ) : <span style={{color:'#EF4444', fontSize:'0.75rem', fontWeight:'bold'}}>£0 (Ineligible)</span>}
                                                </td>

                                                {/* BENEFIT:COST RATIO */}
                                                <td style={{...tdStyle, fontWeight: 'bold', color: bid.isEligible ? '#0D9488' : '#475569', backgroundColor: 'rgba(13, 148, 136, 0.05)'}}>
                                                    {bid.isEligible ? `${(bid.benefitCostRatio || 0).toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}:1` : '-'}
                                                </td>

                                                <td style={tdStyle}>£{bid.calcPricePerUnit.toLocaleString('en-GB', {maximumSignificantDigits: 4})}</td>
                                                <td style={tdStyle}>
                                                    <button
                                                        onClick={() => bid.parcel ? setFocusParcel(bid.parcel.geometry) : setZoomTarget(bid.fieldLocation)}
                                                        disabled={!bid.parcel && !bid.fieldLocation}
                                                        style={{...styles.smBtn, backgroundColor: '#0D9488', width: 'auto', padding: '4px 8px', opacity: (!bid.parcel && !bid.fieldLocation) ? 0.5 : 1, cursor: (!bid.parcel && !bid.fieldLocation) ? 'not-allowed' : 'pointer'}}
                                                        title={bid.parcel || bid.fieldLocation ? 'Zoom to Field' : 'No field location available'}
                                                    >🔍</button>
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{padding:'2px 6px', borderRadius:'4px', fontSize:'0.7rem', fontWeight:'bold', backgroundColor: '#FFFFFF', color: statusColor, border: `1px solid ${statusColor}`}}>
                                                        {displayStatus}
                                                    </span>
                                                    <OverrideBadge bid={bid} />
                                                </td>
                                                <td style={tdStyle}>
                                                    <button
                                                        onClick={() => setExpandedBreakdownId(expandedBreakdownId === bid._id ? null : bid._id)}
                                                        disabled={!bid.outcomeBreakdown?.length}
                                                        style={{...styles.smBtn, backgroundColor: '#475569', width: 'auto', padding: '4px 8px', opacity: bid.outcomeBreakdown?.length ? 1 : 0.4, cursor: bid.outcomeBreakdown?.length ? 'pointer' : 'not-allowed'}}
                                                        title={bid.outcomeBreakdown?.length ? 'Show outcome breakdown' : 'No outcome breakdown available'}
                                                    >{expandedBreakdownId === bid._id ? '▴' : '▾'}</button>
                                                </td>
                                                {showActionsColumn && (
                                                    <td style={tdStyle}>
                                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                            {isClosed && canOverride && bid.status === 'accepted' && (
                                                                <button
                                                                    onClick={() => openOverrideModal('withdraw', bid)}
                                                                    title="Withdraw this winning bid post-closure"
                                                                    style={{...styles.smBtn, backgroundColor: '#F59E0B', width: 'auto', padding: '4px 8px'}}
                                                                >⚠</button>
                                                            )}
                                                            {isClosed && canOverride && ['rejected', 'explore'].includes(bid.status) && (
                                                                <button
                                                                    onClick={() => openOverrideModal('promote', bid)}
                                                                    title="Manually promote this bid to Winning"
                                                                    style={{...styles.smBtn, backgroundColor: '#0D9488', width: 'auto', padding: '4px 8px'}}
                                                                >⭐</button>
                                                            )}
                                                            {handleDeleteBid && (
                                                                <button
                                                                    onClick={() => promptDeleteBid(bid)}
                                                                    disabled={isClosed}
                                                                    title="Force-delete this bid"
                                                                    style={{...styles.smBtn, backgroundColor: '#DC2626', width: 'auto', padding: '4px 8px', opacity: isClosed ? 0.5 : 1, cursor: isClosed ? 'not-allowed' : 'pointer'}}
                                                                >🗑</button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                            {expandedBreakdownId === bid._id && bid.outcomeBreakdown?.length > 0 && (
                                                <tr key={`${bid._id}-breakdown`} style={{ backgroundColor: '#F8FAFC' }}>
                                                    <td colSpan={showActionsColumn ? 11 : 10} style={{ padding: '10px 16px' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{...thStyle, padding: '4px 8px'}}>Outcome</th>
                                                                    <th style={{...thStyle, padding: '4px 8px'}}>Outcome units</th>
                                                                    <th style={{...thStyle, padding: '4px 8px'}}>Spatial Factors</th>
                                                                    <th style={{...thStyle, padding: '4px 8px'}}>Benefit Value (weighted)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {bid.outcomeBreakdown.map((o, oi) => {
                                                                    // Spatial Factors: the zone-based spatialMultiplier (Auction
                                                                    // Focus Area weight) applies to every outcome on this bid;
                                                                    // gisFactors are per-outcome GIS layer factors baked into the
                                                                    // formula's own rate calc. Show whichever are non-default so
                                                                    // this column is never blank when spatial data is active.
                                                                    const spatialParts = [];
                                                                    if (o.spatialMultiplier != null && o.spatialMultiplier !== 1) {
                                                                        spatialParts.push(`Zone: x${Number(o.spatialMultiplier).toFixed(2)}`);
                                                                    }
                                                                    if (o.gisFactors && Object.keys(o.gisFactors).length > 0) {
                                                                        Object.entries(o.gisFactors).forEach(([k, v]) => {
                                                                            spatialParts.push(`${k}: x${Number(v).toFixed(2)}`);
                                                                        });
                                                                    }

                                                                    return (
                                                                        <tr key={oi}>
                                                                            <td style={{padding: '4px 8px'}}>{o.outcomeName || o.algorithmName}</td>
                                                                            <td style={{padding: '4px 8px'}}>{(o.physicalUnits || 0).toLocaleString('en-GB', {maximumFractionDigits: 2})}</td>
                                                                            <td style={{padding: '4px 8px', color: '#475569'}}>
                                                                                {spatialParts.length > 0 ? spatialParts.join(', ') : 'None (x1.00)'}
                                                                            </td>
                                                                            <td style={{padding: '4px 8px', fontWeight: 'bold'}}>£{(o.monetaryValue || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                            </React.Fragment>
                                            )})}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>

        {overrideModal && (
            <AdminBidOverrideModal
                mode={overrideModal.mode}
                bid={overrideModal.bid}
                auctionId={effectiveAuctionId}
                onClose={() => setOverrideModal(null)}
                onWithdrawWinner={handleWithdrawWinner}
                onPromoteBid={handlePromoteBid}
                fetchPromotionHeadroom={fetchPromotionHeadroom}
            />
        )}

        {isBackfillModalOpen && (
            <AdminBackfillModal
                auctionId={effectiveAuctionId}
                onClose={() => setIsBackfillModalOpen(false)}
                onFetchPreview={fetchBackfillPreview}
                onConfirmBackfill={handleBackfillWinners}
            />
        )}
    </div>
    );
};

export default AdminBidDetails;
